import { useCallback, useMemo } from "react"
import {
  PaymentFrequencyEnum,
  VariableCompensationConfiguration,
  VariableCompensationLabelEnum,
  VariableCompensationTypeEnum,
} from "@oysterhr/schema"
import { gql } from "graphql-tag"
import { useQuery } from "@oysterhr/core"
import { optionalProperty } from "~/app/utils/helpers"

const ConfigurationBooleanFieldFragment = gql`
  fragment ConfigurationBooleanFieldFragment on ConfigurationBooleanField {
    default
    editable
  }
`
const ConfigurationPaymentFrequencyEnumFieldFragment = gql`
  fragment ConfigurationPaymentFrequencyEnumFieldFragment on ConfigurationPaymentFrequencyEnumField {
    default
    editable
  }
`

const VariableCompensationConfigurations = gql`
  query VariableCompensationConfigurations($isContractor: Boolean) {
    variableCompensationConfigurations(isContractor: $isContractor) {
      compensationType
      guaranteed {
        ...ConfigurationBooleanFieldFragment
      }
      includedInGrossSalary {
        ...ConfigurationBooleanFieldFragment
      }
      label
      paymentFrequency {
        ...ConfigurationPaymentFrequencyEnumFieldFragment
      }
      percentage {
        ...ConfigurationBooleanFieldFragment
      }
      statutory {
        ...ConfigurationBooleanFieldFragment
      }
      prorated {
        ...ConfigurationBooleanFieldFragment
      }
    }
  }

  ${ConfigurationBooleanFieldFragment}
  ${ConfigurationPaymentFrequencyEnumFieldFragment}
`

export type VariableCompensationConfigurationResult = {
  statutory?: boolean
  guaranteed?: boolean
  includedInGrossSalary?: boolean
  paymentFrequency?: PaymentFrequencyEnum | null
  percentage?: boolean
  prorated?: boolean
}

interface VariableCompensationConfigurationLookup {
  label?: VariableCompensationLabelEnum
}

type VariableCompensationConfigurableFields = Omit<
  VariableCompensationConfiguration,
  "__typename" | "label" | "compensationType"
>

export const useVariableCompensationConfigurations = ({
  compensationType,
  isContractor,
}: {
  compensationType: VariableCompensationTypeEnum
  isContractor?: boolean
}) => {
  const { data } = useQuery<{
    variableCompensationConfigurations: VariableCompensationConfiguration[]
    isContractor?: boolean
  }>(VariableCompensationConfigurations, { variables: { isContractor } })
  const allConfigurations = useMemo(() => {
    return data?.variableCompensationConfigurations.reduce(
      (acc, config) => {
        if (config.compensationType === compensationType) {
          acc[config.label] = config
        }
        return acc
      },
      {} as {
        [key in VariableCompensationLabelEnum]: VariableCompensationConfiguration
      },
    )
  }, [data, compensationType])

  const getConfigByLabel = useCallback(
    (label?: VariableCompensationLabelEnum) => {
      if (!label) return null
      return allConfigurations?.[label]
    },
    [allConfigurations],
  )

  const isPercentage = useCallback(
    (label?: VariableCompensationLabelEnum) => {
      if (!label) return false
      return getConfigByLabel(label)?.percentage?.default
    },
    [getConfigByLabel],
  )

  const isEditable = useCallback(
    ({
      label,
      key,
      currentValue,
    }: {
      label?: VariableCompensationLabelEnum
      key: keyof VariableCompensationConfigurableFields
      currentValue?: boolean | PaymentFrequencyEnum | null
    }) => {
      const config = getConfigByLabel(label)?.[key]
      const hasConfig = config != null
      const hasValue = currentValue != null
      const valueIsNotDefault = config?.default !== currentValue
      if (!hasConfig || (hasValue && valueIsNotDefault)) return true
      return !!config?.editable
    },
    [getConfigByLabel],
  )

  const getInitialValues = useCallback(
    (lookupOptions: VariableCompensationConfigurationLookup) => {
      const config = getConfigByLabel(lookupOptions.label)
      return {
        ...optionalProperty("percentage", config?.percentage?.default),
        ...optionalProperty("paymentFrequency", config?.paymentFrequency?.default),
        ...optionalProperty("includedInGrossSalary", config?.includedInGrossSalary?.default),
        ...optionalProperty("guaranteed", config?.guaranteed?.default),
        ...optionalProperty("statutory", config?.statutory?.default),
        ...optionalProperty("prorated", config?.prorated?.default),
      } as VariableCompensationConfigurationResult
    },
    [getConfigByLabel],
  )

  return {
    allConfigurations,
    getInitialValues,
    getConfigByLabel,
    isPercentage,
    isEditable,
  }
}
